// Strong Admin/SuperAdmin sessions (openspec/changes/add-rebels-admin-security-and-mcp-connectors, task R4.1; port of
// the QPortal live frontend). The access token lives only in memory; the refresh secret is the HttpOnly cookie
// rb_admin_session that the browser sends to the admin-session endpoints. localStorage holds only a non-secret marker so
// a reload knows it should try to restore the session. Professionals keep their existing token storage.
const MARKER = 'REBELS_ADMIN_SESSION';
// [Rebels] Anti-CSRF header required by the cookie endpoints (backend AdminSecurityService.CookieRequestHeader).
const CSRF_HEADER = 'X-Rebels-Admin';
const baseURL = process.env.REACT_APP_API_BASE_URL || '';
export class AdminSecurityError extends Error {
    status;
    code;
    retryAfterSeconds;
    constructor(status, code, retryAfterSeconds) {
        super(code);
        this.status = status;
        this.code = code;
        this.retryAfterSeconds = retryAfterSeconds;
    }
}
const secondsOrUndefined = (value) => {
    const seconds = typeof value === 'number' ? value : typeof value === 'string' ? Number.parseInt(value, 10) : NaN;
    return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
};
let accessToken = null;
let refreshing = null;
const listeners = new Set();
const hasMarker = () => {
    try {
        return localStorage.getItem(MARKER) === '1';
    }
    catch {
        return false;
    }
};
const setMarker = (on) => {
    try {
        if (on)
            localStorage.setItem(MARKER, '1');
        else
            localStorage.removeItem(MARKER);
    }
    catch {
        // Storage may be blocked; the session then simply does not survive a reload.
    }
};
const notify = () => listeners.forEach((listener) => listener(accessToken));
// Cookie-bearing calls: exact-origin CORS with credentials plus the anti-CSRF header.
export const cookieRequest = async (path, body) => {
    const response = await fetch(baseURL + path, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER]: '1' },
        body: JSON.stringify(body ?? {})
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok)
        throw new AdminSecurityError(response.status, data?.code || (response.status === 429 ? 'RATE_LIMITED' : undefined) || data?.title || 'REQUEST_FAILED', secondsOrUndefined(data?.retryAfterSeconds) ?? secondsOrUndefined(response.headers?.get?.('Retry-After')));
    return data;
};
// Rate limiting, server errors, network failures and a refresh race that did not settle are transient: they must
// not end a session that may still be valid. Only an explicit rejection (401/403, or the capability being off) does.
const isTransient = (error) => !(error instanceof AdminSecurityError) ||
    error.status === 429 ||
    error.status >= 500 ||
    error.code === 'RATE_LIMITED' ||
    error.code === 'REFRESH_RACE';
const AdminSession = {
    accessToken: () => accessToken,
    active: () => accessToken !== null || hasMarker(),
    subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
    adopt(result) {
        accessToken = result.accessToken;
        setMarker(true);
        notify();
        return result;
    },
    clear() {
        accessToken = null;
        setMarker(false);
        notify();
    },
    // Single-flight rotation. A 409 means another tab rotated a moment ago; its new cookie is already set.
    refresh() {
        if (!refreshing) {
            refreshing = (async () => {
                for (let attempt = 0;; attempt++) {
                    try {
                        return AdminSession.adopt(await cookieRequest('account/admin-session/refresh'));
                    }
                    catch (error) {
                        if (error instanceof AdminSecurityError && error.code === 'REFRESH_RACE' && attempt < 3) {
                            await new Promise((resolve) => setTimeout(resolve, 600 + attempt * 400));
                            continue;
                        }
                        if (error instanceof AdminSecurityError && (error.status === 401 || error.status === 403))
                            AdminSession.clear();
                        throw error;
                    }
                }
            })().finally(() => {
                refreshing = null;
            });
        }
        return refreshing;
    },
    // Called once at start-up: restores a strong session after a reload, if one exists. Without the marker nothing is
    // requested, so professionals and admins without a strong session see no new network call. A transient failure
    // (429 or network) keeps the marker so the next reload can still restore the cookie session.
    async restore() {
        if (!hasMarker())
            return null;
        try {
            return (await AdminSession.refresh()).accessToken;
        }
        catch (error) {
            if (!isTransient(error))
                AdminSession.clear();
            return null;
        }
    },
    // [Rebels] The logout route answers 404 while the capability is off (gate R3); the local sign-out never waits for it.
    async logout() {
        const hadSession = AdminSession.active();
        AdminSession.clear();
        if (hadSession)
            await cookieRequest('account/admin-session/logout').catch(() => undefined);
    }
};
export default AdminSession;
